import React, { useState } from 'react';
import FAQAssist from './FAQAssist';

const ForeignContact: React.FC = () => {
  const [hasForeignContact, setHasForeignContact] = useState<string>('');

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Foreign Contact</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Foreign Contact:</strong> Report any foreign contacts that meet DCSA reporting requirements
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Foreign Contact Question with FAQ */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex-1">
                  Do you have any foreign contacts that need to be reported?
                </h3>
                <FAQAssist 
                  topic="foreign-contact-reporting"
                  fieldContext={{
                    fieldType: 'foreign-contact-determination',
                    fieldDescription: 'Determination of whether foreign contacts meet DCSA reporting requirements',
                    significance: 'Critical for security clearance assessment and ongoing monitoring',
                    commonRejectionReasons: [
                      'Failure to report required foreign contacts',
                      'Incomplete or vague contact descriptions',
                      'Not understanding reporting requirements'
                    ],
                    interviewQuestions: [
                      'What foreign contacts do you have and what is the nature of these relationships?',
                      'How frequently do you interact with these foreign contacts?',
                      'Are any of these contacts in positions of authority or have access to sensitive information?'
                    ],
                    finalPrompt: 'Based on DCSA reporting requirements, I have determined:',
                    helpText: 'Use the FAQ Assistant to understand foreign contact reporting requirements and get guidance on your specific situation.',
                    bestPractices: [
                      'Review DCSA guidance on foreign contact reporting',
                      'Consider frequency and nature of contact',
                      'Evaluate potential for foreign influence'
                    ],
                    warningFlags: [
                      'Simply saying "I don\'t know"',
                      'Not researching reporting requirements',
                      'Ignoring borderline cases'
                    ],
                    expectedLength: 'moderate',
                    requiresSpecificity: true
                  }}
                />
              </div>
              
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasForeignContact"
                    value="yes"
                    checked={hasForeignContact === 'yes'}
                    onChange={(e) => setHasForeignContact(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasForeignContact"
                    value="no"
                    checked={hasForeignContact === 'no'}
                    onChange={(e) => setHasForeignContact(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">No</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasForeignContact"
                    value="unsure"
                    checked={hasForeignContact === 'unsure'}
                    onChange={(e) => setHasForeignContact(e.target.value)}
                    className="h-4 w-4 text-gov-blue focus:ring-gov-blue border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">I'm not sure</span>
                </label>
              </div>

              {/* Additional context for unsure selection */}
              {hasForeignContact === 'unsure' && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-amber-900 mb-1">Need Help Determining Reporting Requirements?</h4>
                      <p className="text-sm text-amber-800">
                        Use the FAQ Assistant above to get guidance on what constitutes a reportable foreign contact, 
                        or describe your specific situation for a personalized assessment.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Future expansion area */}
            {hasForeignContact === 'yes' && (
              <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Foreign Contact Details</h3>
                  <p className="text-sm text-gray-600">
                    Additional fields for foreign contact details will be implemented in the next phase.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between">
            <button className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
              Previous
            </button>
            <div className="space-x-3">
              <button className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                Save Progress
              </button>
              <button className="px-6 py-2 bg-gov-blue text-white rounded-md hover:bg-blue-800">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForeignContact;